import { useState } from "react";
import { type CalendarRecurrence, MAX_RECURRENCE_INTERVAL, recurrenceText } from "@probetalab/contracts";
import { Input, Segmented, Select } from "./ui.js";

/**
 * «Se repite» of a calendar note (migration 0031; Fer 2026-11-03: the electricity bill comes "cada
 * dos meses"). The common choices are one tap — Una vez · Cada semana · Cada mes · Cada 2 meses — and
 * «Otro…» opens "Cada [N] [semanas | meses]" for anything else (N 1–12). A repeating note may end on a
 * day («Hasta», optional). The line under it says the rule back in words.
 */

export interface RecurrenceValue {
  recurrence: CalendarRecurrence;
  interval: number;
  until: string | null;
}

export type RecurrencePreset = "none" | "week" | "month" | "two-months" | "other";

export function presetOf(v: Pick<RecurrenceValue, "recurrence" | "interval">): RecurrencePreset {
  if (v.recurrence === "none") return "none";
  if (v.recurrence === "weekly" && v.interval === 1) return "week";
  if (v.recurrence === "monthly" && v.interval === 1) return "month";
  if (v.recurrence === "monthly" && v.interval === 2) return "two-months";
  return "other";
}

/** The value after tapping a preset; «Otro…» keeps a custom rule, else starts at every 3 months. */
export function applyPreset(v: RecurrenceValue, p: RecurrencePreset): RecurrenceValue {
  switch (p) {
    case "none":
      return { recurrence: "none", interval: 1, until: null };
    case "week":
      return { ...v, recurrence: "weekly", interval: 1 };
    case "month":
      return { ...v, recurrence: "monthly", interval: 1 };
    case "two-months":
      return { ...v, recurrence: "monthly", interval: 2 };
    case "other":
      return presetOf(v) === "other" ? v : { ...v, recurrence: "monthly", interval: 3 };
  }
}

/** A typed interval, clamped to 1–12; null while the box is empty or not a number. */
export function parseInterval(text: string): number | null {
  if (!/^\d{1,2}$/.test(text.trim())) return null;
  const n = Number(text.trim());
  return n >= 1 && n <= MAX_RECURRENCE_INTERVAL ? n : null;
}

export function RecurrenceField({ value, onChange, firstDay }: { value: RecurrenceValue; onChange: (v: RecurrenceValue) => void; firstDay: string }) {
  // «Otro…» stays open while typing, even when the rule happens to equal a preset (2 months).
  const [custom, setCustom] = useState(() => presetOf(value) === "other");
  const preset: RecurrencePreset = custom ? "other" : presetOf(value);
  const summary = recurrenceText({ recurrence: value.recurrence, recurrence_interval: value.interval, recurrence_until: value.until });
  const untilBad = value.until !== null && value.until < firstDay;
  return (
    <div className="field recurrence-field" role="group" aria-label="Se repite">
      <span className="label">Se repite</span>
      <Segmented<RecurrencePreset>
        value={preset}
        onChange={(p) => {
          setCustom(p === "other");
          onChange(applyPreset(value, p));
        }}
        options={[
          { value: "none", label: "Una vez" },
          { value: "week", label: "Cada semana" },
          { value: "month", label: "Cada mes" },
          { value: "two-months", label: "Cada 2 meses" },
          { value: "other", label: "Otro…" },
        ]}
      />
      {preset === "other" && (
        <div className="recurrence-custom">
          <span>Cada</span>
          <Input
            aria-label="Cada cuántos"
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_RECURRENCE_INTERVAL}
            value={String(value.interval)}
            style={{ width: 84 }}
            onChange={(e) => {
              const n = parseInterval(e.target.value);
              if (n !== null) onChange({ ...value, interval: n });
            }}
          />
          <Select aria-label="Unidad de repetición" value={value.recurrence} style={{ width: "auto" }} onChange={(e) => onChange({ ...value, recurrence: e.target.value as CalendarRecurrence })}>
            <option value="weekly">{value.interval === 1 ? "semana" : "semanas"}</option>
            <option value="monthly">{value.interval === 1 ? "mes" : "meses"}</option>
          </Select>
        </div>
      )}
      {value.recurrence !== "none" && (
        <div className="recurrence-until">
          <label className="recurrence-until-label">
            <span>Hasta</span> <span className="opt">(opcional)</span>
            <Input aria-label="Se repite hasta" type="date" min={firstDay} value={value.until ?? ""} onChange={(e) => onChange({ ...value, until: e.target.value || null })} style={{ width: "auto" }} />
          </label>
          {value.until && (
            <button type="button" className="link-btn" onClick={() => onChange({ ...value, until: null })}>
              Sin fecha final
            </button>
          )}
        </div>
      )}
      {untilBad ? <span className="err">La fecha final es antes de la primera.</span> : summary ? <span className="hint" data-testid="recurrence-summary">{summary}.</span> : null}
    </div>
  );
}
